import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  KeyRound, ArrowLeft, Sparkles, ArrowRight, Zap, ShieldCheck,
  Smartphone, Coffee, MapPin, MessageCircle, Mail, Share2, Copy, Check,
} from 'lucide-react';

const SHARE_URL = 'https://docsite.care/whats-new/pin-fast-login';
const SHARE_MESSAGE = (
  `New on DocSite — set a 6-digit PIN once and every login becomes instant. ` +
  `No more waiting for SMS. SMS still kicks in automatically the first time you sign in on a brand-new device, so your account stays secure.\n\n` +
  `1-pager: ${SHARE_URL}`
);

export default function PinFastLoginLaunch() {
  const [copied, setCopied] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(SHARE_MESSAGE)}`;
  const mailHref = `mailto:?subject=${encodeURIComponent("DocSite — sign in instantly with a 6-digit PIN")}&body=${encodeURIComponent(SHARE_MESSAGE)}`;
  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'DocSite — instant sign-in PIN', text: SHARE_MESSAGE, url: SHARE_URL }); }
      catch { /* cancelled */ }
    } else { copyLink(); }
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(SHARE_URL); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { /* clipboard denied */ }
  };

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Helmet>
        <title>DocSite — Sign in instantly with a 6-digit PIN</title>
        <meta name="description" content="Set a 6-digit DocSite PIN once and every login on your phone, laptop or clinic computer becomes instant. SMS still protects you on brand-new devices." />
        <meta name="robots" content="index, follow" />
        <meta name="keywords" content="DocSite PIN login, fast doctor login, 6-digit PIN, instant sign-in, secure doctor app India" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="DocSite — Sign in instantly with a 6-digit PIN" />
        <meta property="og:description" content="Set a PIN once. Every login becomes instant. SMS still protects you on new devices." />
        <meta property="og:url" content={SHARE_URL} />
        <meta property="article:published_time" content="2026-02-07" />
        <link rel="canonical" href={SHARE_URL} />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org', '@type': 'NewsArticle',
          headline: 'Sign in instantly with a 6-digit PIN',
          description: 'Set a 6-digit DocSite PIN once and every future login becomes instant. SMS protection kicks in automatically on brand-new devices.',
          datePublished: '2026-02-07', dateModified: '2026-02-07',
          author: { '@type': 'Organization', name: 'DocSite', url: 'https://docsite.care' },
          publisher: { '@type': 'Organization', name: 'DocSite', url: 'https://docsite.care',
            logo: { '@type': 'ImageObject', url: 'https://docsite.care/logo512.png' } },
          mainEntityOfPage: SHARE_URL,
        })}</script>
      </Helmet>

      <div className="max-w-2xl mx-auto px-5 sm:px-6 py-8 sm:py-12">
        <Link to="/whats-new" data-testid="pin-launch-back"
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-6">
          <ArrowLeft size={16} /> All updates
        </Link>

        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
          {/* Hero */}
          <div className="bg-gradient-to-br from-violet-600 via-violet-500 to-sky-500 text-white p-6 sm:p-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-xs font-bold uppercase tracking-widest mb-4">
              <Sparkles size={14} /> Just Launched · 7 Feb 2026
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Sign in instantly with a 6-digit PIN
            </h1>
            <p className="text-violet-50 mt-3 text-sm sm:text-base leading-relaxed">
              Set your PIN once. Every future login on any device you've used before becomes one tap.
              SMS verification still kicks in automatically the first time you sign in on a brand-new device — so your account stays as safe as ever.
            </p>
          </div>

          {/* What you'll feel */}
          <div className="p-6 sm:p-8 space-y-6">
            <div className="grid sm:grid-cols-3 gap-3">
              <Pill icon={<Zap size={18} className="text-violet-600" />} title="Instant" body="Sign in in under a second. No SMS wait." testid="pin-pill-instant" />
              <Pill icon={<ShieldCheck size={18} className="text-emerald-600" />} title="Still secure" body="New device? SMS verification kicks in automatically." testid="pin-pill-secure" />
              <Pill icon={<Smartphone size={18} className="text-sky-600" />} title="Works everywhere" body="Phone, laptop, clinic computer — same PIN, every screen." testid="pin-pill-everywhere" />
            </div>

            {/* How it works in 30 seconds */}
            <div className="rounded-2xl border-2 border-violet-200 bg-violet-50 p-5">
              <p className="text-[10px] font-bold text-violet-700 uppercase tracking-widest mb-2">Switch this on (30 seconds)</p>
              <ol className="space-y-2 text-sm text-slate-800 list-decimal list-inside">
                <li>Sign in to your DocSite dashboard like you always do.</li>
                <li>Open your <span className="font-semibold">Profile</span> tab — you'll see "Fast sign-in with a 6-digit PIN".</li>
                <li>Tap <span className="font-semibold">Set PIN</span>, type 6 digits you'll remember, confirm. Done.</li>
              </ol>
              <p className="text-xs text-violet-800 mt-3">From your very next login on this device, you'll just type your PIN. No SMS. No notification taps. Straight in.</p>
            </div>

            {/* Real-life scenarios */}
            <div className="space-y-3">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">How this helps you in real life</p>
              <Scenario
                icon={<Coffee size={18} className="text-amber-600" />}
                title="Morning coffee, 8:55 AM"
                body="Your first patient is in 5 minutes. You open DocSite on your clinic laptop, type your 6-digit PIN, and your dashboard is ready. No fumbling for your phone, no waiting for the SMS to arrive."
                testid="pin-scen-morning"
              />
              <Scenario
                icon={<Smartphone size={18} className="text-sky-600" />}
                title="Quick check between patients"
                body="You want to peek at today's connects on your phone. Tap the DocSite icon, PIN, in. Quick enough to do between two patients without losing rhythm."
                testid="pin-scen-quick"
              />
              <Scenario
                icon={<MapPin size={18} className="text-emerald-600" />}
                title="Travelling for a conference"
                body="You sign in from a hotel laptop in Bengaluru. DocSite notices it's a brand-new device, sends an SMS to your phone — only this once. Tomorrow morning when you're back in your home clinic, your usual PIN works straight away."
                testid="pin-scen-travel"
              />
              <Scenario
                icon={<ShieldCheck size={18} className="text-rose-600" />}
                title="Lost your laptop"
                body="Open Profile → Trusted Devices → 'Sign out everywhere'. Every device is logged out instantly. Whoever finds the laptop can't get into your dashboard, even if they know your password — they'd still need the SMS code on your phone."
                testid="pin-scen-lost"
              />
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Link to="/dashboard" data-testid="pin-launch-cta-dashboard"
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-semibold text-sm shadow-sm">
                Set my PIN now <ArrowRight size={16} />
              </Link>
              <Link to="/whats-new" data-testid="pin-launch-cta-allupdates"
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold text-sm">
                See all updates
              </Link>
            </div>

            {/* Share */}
            <div className="pt-4 border-t border-slate-200">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3">Tell a colleague</p>
              <div className="flex flex-wrap gap-2">
                <a href={whatsappHref} target="_blank" rel="noopener noreferrer" data-testid="pin-launch-share-whatsapp"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-semibold border border-emerald-200">
                  <MessageCircle size={14} /> WhatsApp
                </a>
                <a href={mailHref} data-testid="pin-launch-share-email"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-sky-50 hover:bg-sky-100 text-sky-700 text-xs font-semibold border border-sky-200">
                  <Mail size={14} /> Email
                </a>
                <button onClick={nativeShare} data-testid="pin-launch-share-native"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-700 text-xs font-semibold border border-slate-200">
                  <Share2 size={14} /> Share
                </button>
                <button onClick={copyLink} data-testid="pin-launch-share-copy"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-700 text-xs font-semibold border border-slate-200">
                  {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy link</>}
                </button>
              </div>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-slate-500 mt-6">
          <KeyRound size={12} className="inline mr-1" />Free on every plan. Forgot your PIN? Reset via email anytime.
        </p>
      </div>
    </div>
  );
}

function Pill({ icon, title, body, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1.5">{icon}<p className="text-sm font-semibold text-slate-900">{title}</p></div>
      <p className="text-xs text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}

function Scenario({ icon, title, body, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-start gap-3" data-testid={testid}>
      <div className="w-9 h-9 rounded-lg bg-slate-50 flex items-center justify-center shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900">{title}</p>
        <p className="text-xs text-slate-600 mt-1 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
